import ErrorPage from 'components/ErrorPage'
import { buildTranslate } from 'locales'
import React from 'react'
import { errorHandler } from 'utilities/errorHandler'

const t = buildTranslate('errors')

type Props = {
  children: React.ReactNode
}

type State = {
  hasError: boolean
}

/**
 * Safety net for the app tree. Mounted in AppProviders just inside
 * StyleProvider + ReduxProvider (both required to render the fallback), so it
 * catches throws from the socket/AI/flags providers and everything below —
 * App, the router, and all scenes — that would otherwise blank #root. Throws in
 * StyleProvider/ReduxProvider themselves remain above it and are out of reach.
 *
 * Error boundaries must be class components — there is no hook equivalent.
 */
class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    errorHandler.captureException(error, {
      componentStack: info.componentStack,
    })
  }

  render() {
    if (this.state.hasError) {
      // Root boundary sits above the router, so a crash unmounts it — a reload
      // is the recovery path. Give the user an explicit way to trigger it, and
      // use crash-specific copy (not ErrorPage's partial-data-load default).
      return (
        <ErrorPage
          title={t('app_error_title')}
          message={t('app_error_message')}
          onReload={() => window.location.reload()}
        />
      )
    }
    return this.props.children
  }
}

export default ErrorBoundary
